import { Clock, Context, Data, Effect, Option } from 'effect';

import type { CanonicalBbox } from './types';

/** Eager in-memory document bytes. */
export interface BytesDocumentRef {
  readonly kind: 'bytes';
  readonly documentId: string;
  readonly bytes: Uint8Array;
  readonly fileName?: string;
  readonly mimeType?: string;
  readonly pageCount?: number;
}

/**
 * Lazy document reference held in object storage. Materialized through the
 * `DocumentSource` host service; bucket/key never appear in error payloads.
 */
export interface StorageDocumentRef {
  readonly kind: 'storage';
  readonly documentId: string;
  readonly provider: 'r2' | 'gcs';
  readonly bucket: string;
  readonly key: string;
  readonly fileName?: string;
  readonly mimeType?: string;
  readonly pageCount?: number;
}

export type ParseDocumentRef = BytesDocumentRef | StorageDocumentRef;

export function bytesRef(input: {
  readonly documentId: string;
  readonly bytes: Uint8Array;
  readonly fileName?: string;
  readonly mimeType?: string;
  readonly pageCount?: number;
}): ParseDocumentRef {
  return { kind: 'bytes', ...input };
}

export function storageRef(input: {
  readonly documentId: string;
  readonly provider: 'r2' | 'gcs';
  readonly bucket: string;
  readonly key: string;
  readonly fileName?: string;
  readonly mimeType?: string;
  readonly pageCount?: number;
}): ParseDocumentRef {
  return { kind: 'storage', ...input };
}

export function isBytesRef(document: ParseDocumentRef): document is BytesDocumentRef {
  return document.kind === 'bytes';
}

export function isStorageRef(document: ParseDocumentRef): document is StorageDocumentRef {
  return document.kind === 'storage';
}

export interface TextItem {
  readonly str: string;
  readonly width?: number;
  readonly height?: number;
  readonly transform?: readonly number[];
}

export interface PageImage {
  readonly bytes: Uint8Array;
  readonly mimeType: string;
  readonly width: number;
  readonly height: number;
}

export interface ParserHttpService {
  readonly fetch: (
    input: string | URL,
    init?: RequestInit,
  ) => Effect.Effect<Response, ParserDependencyError>;
}

export class ParserHttp extends Context.Service<ParserHttp, ParserHttpService>()(
  'okrapdf/parser-sdk/ParserHttp',
) {}

export interface PageRegionClip {
  /** Optional normalized page region; hosts return the clipped page artifact. */
  readonly region?: CanonicalBbox;
}

export interface PageSourceService {
  readonly countPages: (document: ParseDocumentRef) => Effect.Effect<number, ParserDependencyError>;
  readonly pagePdf: (
    document: ParseDocumentRef,
    page: number,
    clip?: PageRegionClip,
  ) => Effect.Effect<Uint8Array, ParserDependencyError>;
  readonly pageImage: (
    document: ParseDocumentRef,
    page: number,
    options: { readonly dpi: number; readonly format: 'png' | 'jpeg' },
    clip?: PageRegionClip,
  ) => Effect.Effect<PageImage, ParserDependencyError>;
}

export class PageSource extends Context.Service<PageSource, PageSourceService>()(
  'okrapdf/parser-sdk/PageSource',
) {}

export interface PdfTextLayerPage {
  readonly items: readonly TextItem[];
  readonly width: number;
  readonly height: number;
}

export interface PdfTextLayerService {
  readonly extractPage: (
    document: ParseDocumentRef,
    page: number,
  ) => Effect.Effect<PdfTextLayerPage, ParserDependencyError>;
}

export class PdfTextLayer extends Context.Service<PdfTextLayer, PdfTextLayerService>()(
  'okrapdf/parser-sdk/PdfTextLayer',
) {}

export interface ParserSecretsService {
  readonly get: (name: string) => Effect.Effect<Option.Option<string>>;
}

export class ParserSecrets extends Context.Service<ParserSecrets, ParserSecretsService>()(
  'okrapdf/parser-sdk/ParserSecrets',
) {}

// ── Parser tracer (optional telemetry; warning-only, never in the error channel) ──

export type ParserSpanAttributeValue = string | number | boolean;
export type ParserSpanAttributes = Readonly<Record<string, ParserSpanAttributeValue | undefined>>;

export interface ParserTraceUsage {
  readonly inputTokens?: number;
  readonly outputTokens?: number;
  readonly totalTokens?: number;
  readonly costUsd?: number;
}

/**
 * One safe parser span. `name` is the `Effect.fn`/span name; attributes are
 * filtered to known surface/transport labels. Secrets, prompts, document bytes/text, image data, and
 * URLs carrying tokens never belong here.
 */
export interface ParserTracePage {
  readonly name: string;
  readonly parserId: string;
  readonly model?: string;
  /** Accepted for compatibility; document names are never exported to telemetry. */
  readonly documentName?: string;
  readonly pageNumber?: number;
  readonly status?: number;
  readonly ok?: boolean;
  readonly elapsedMs?: number;
  readonly usage?: ParserTraceUsage;
  readonly attributes?: ParserSpanAttributes;
}

export type ParserTraceResult =
  | { readonly ok: true; readonly uploaded: number }
  | { readonly ok: false; readonly message: string };

export interface ParserTracerService {
  readonly enabled: Effect.Effect<boolean, never, never>;
  readonly recordPage: (page: ParserTracePage) => Effect.Effect<ParserTraceResult, never, never>;
}

export class ParserTracer extends Context.Service<ParserTracer, ParserTracerService>()(
  'okrapdf/parser-sdk/ParserTracer',
) {}

export const parserTracerNoopService: ParserTracerService = {
  enabled: Effect.succeed(false),
  recordPage: () => Effect.succeed({ ok: true, uploaded: 0 }),
};

export interface LangfuseParserTracerOptions {
  readonly getSecret: (name: string) => string | undefined;
  readonly fetch: typeof globalThis.fetch;
  readonly signal?: AbortSignal;
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function cryptoRandomId(now: number): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  return `${now.toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const TRACE_LABELS = new Set([
  'conformance',
  'parser',
  'api',
  'node',
  'cloudflare',
  'memory',
  'openrouter',
  'vllm',
]);

/** Preserve a closed telemetry vocabulary; arbitrary strings can be document content. */
function safeTraceAttributes(attributes: ParserSpanAttributes | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of ['surface', 'transport']) {
    const value = attributes?.[key];
    if (typeof value === 'string' && TRACE_LABELS.has(value)) result[key] = value;
  }
  return result;
}

function safeTraceNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function safeTraceUsage(usage: ParserTraceUsage | undefined): Record<string, number> | undefined {
  if (!usage) return undefined;
  const result: Record<string, number> = {};
  for (const key of ['inputTokens', 'outputTokens', 'totalTokens', 'costUsd'] as const) {
    const value = safeTraceNumber(usage[key]);
    if (value !== undefined) result[key] = value;
  }
  return result;
}

/**
 * Langfuse-backed ParserTracer. Credentials are read lazily; when absent the
 * service reports `enabled: false`. Ingestion failures are returned as values
 * (`ok: false`) so callers can downgrade to warnings — telemetry never fails a parse.
 */
export function makeLangfuseParserTracerService(
  options: LangfuseParserTracerOptions,
): ParserTracerService {
  const credentials = () => {
    const publicKey =
      options.getSecret('LANGFUSE_PUBLIC_KEY_OKRAPDF') ?? options.getSecret('LANGFUSE_PUBLIC_KEY');
    const secretKey =
      options.getSecret('LANGFUSE_SECRET_KEY_OKRAPDF') ?? options.getSecret('LANGFUSE_SECRET_KEY');
    if (!publicKey || !secretKey) return undefined;
    const host = trimTrailingSlash(
      options.getSecret('LANGFUSE_BASE_URL') ??
        options.getSecret('LANGFUSE_HOST') ??
        'https://us.cloud.langfuse.com',
    );
    return {
      host,
      auth: `Basic ${btoa(`${publicKey}:${secretKey}`)}`,
      sensitive: [publicKey, secretKey],
    };
  };
  return {
    enabled: Effect.sync(() => credentials() !== undefined).pipe(
      Effect.catchDefect(() => Effect.succeed(false)),
    ),
    recordPage: Effect.fn('parser.tracer.recordPage')((page: ParserTracePage) =>
      Effect.gen(function* () {
        const creds = credentials();
        if (!creds) return { ok: true as const, uploaded: 0 };
        const label = (value: string | undefined, fallback?: string) =>
          typeof value === 'string' &&
          /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,159}$/.test(value) &&
          !creds.sensitive.some((secret) => value.includes(secret))
            ? value
            : fallback;
        const parserId = label(page.parserId, 'parser')!;
        const name = label(page.name, 'parser.page')!;
        const model = label(page.model);
        const timestamp = yield* Clock.currentTimeMillis;
        const sessionId = `parser-${parserId}-${cryptoRandomId(timestamp)}`;
        const pageNumber = safeTraceNumber(page.pageNumber);
        const traceId = `${sessionId}-p${String(pageNumber ?? 1).padStart(3, '0')}`;
        const usage = safeTraceUsage(page.usage);
        const metadata: Record<string, unknown> = {
          ...safeTraceAttributes(page.attributes),
          parser_id: parserId,
          ...(model === undefined ? {} : { model }),
          ...(pageNumber === undefined ? {} : { page_number: pageNumber }),
          ...(safeTraceNumber(page.status) === undefined ? {} : { status: page.status }),
          ...(typeof page.ok !== 'boolean' ? {} : { ok: page.ok }),
          ...(safeTraceNumber(page.elapsedMs) === undefined ? {} : { elapsed_ms: page.elapsedMs }),
          ...(usage === undefined ? {} : { usage }),
        };
        const now = new Date(timestamp).toISOString();
        const events: Array<Record<string, unknown>> = [
          {
            id: cryptoRandomId(timestamp),
            type: 'trace-create',
            timestamp: now,
            body: { id: traceId, name, sessionId, tags: ['parser', parserId], metadata },
          },
          {
            id: cryptoRandomId(timestamp),
            type: 'generation-create',
            timestamp: now,
            body: {
              traceId,
              id: `${traceId}-generation`,
              name,
              ...(model === undefined ? {} : { model }),
              metadata: { usage: usage ?? null },
            },
          },
        ];
        if (usage?.costUsd !== undefined)
          events.push({
            id: cryptoRandomId(timestamp),
            type: 'score-create',
            timestamp: now,
            body: { traceId, name: 'cost_usd', value: usage.costUsd, dataType: 'NUMERIC' },
          });
        return yield* Effect.acquireUseRelease(
          Effect.sync(() => new AbortController()),
          (controller) =>
            Effect.gen(function* () {
              const response = yield* Effect.tryPromise({
                try: () =>
                  options.fetch(`${creds.host}/api/public/ingestion`, {
                    method: 'POST',
                    headers: { Authorization: creds.auth, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ batch: events }),
                    signal: options.signal
                      ? AbortSignal.any([options.signal, controller.signal])
                      : controller.signal,
                  }),
                catch: () => 'Langfuse transport unavailable',
              });
              return yield* Effect.acquireUseRelease(
                Effect.succeed(response),
                (response) =>
                  Effect.succeed(
                    response.ok
                      ? { ok: true as const, uploaded: 1 }
                      : {
                          ok: false as const,
                          message: `Langfuse ingestion failed (${response.status})`,
                        },
                  ),
                (response) =>
                  Effect.tryPromise({
                    try: async () => {
                      await response.body?.cancel();
                    },
                    catch: () => undefined,
                  }).pipe(Effect.catch(() => Effect.void)),
              );
            }),
          (controller) => Effect.sync(() => controller.abort()),
        );
      }).pipe(
        Effect.catch(() =>
          Effect.succeed({ ok: false as const, message: 'Langfuse transport unavailable' }),
        ),
        Effect.catchDefect(() =>
          Effect.succeed({ ok: false as const, message: 'Langfuse telemetry unavailable' }),
        ),
      ),
    ),
  };
}

/**
 * Materializes lazy document references. Bytes refs pass through; storage
 * refs resolve through the pack's bound provider (R2 on Cloudflare, GCS on
 * Node, fixture map in memory). Bucket/key stay out of error payloads.
 */
export interface DocumentSourceService {
  readonly bytes: (document: ParseDocumentRef) => Effect.Effect<Uint8Array, ParserDependencyError>;
}

export class DocumentSource extends Context.Service<DocumentSource, DocumentSourceService>()(
  'okrapdf/parser-sdk/DocumentSource',
) {}

/** Materialize any document ref through the ambient DocumentSource. */
export const documentBytesEffect = Effect.fn('okrapdf/parser-sdk.documentBytes')(function* (
  document: ParseDocumentRef,
) {
  if (document.kind !== 'storage') return document.bytes;
  const source = yield* DocumentSource;
  return yield* source.bytes(document);
});

/**
 * Bytes-only DocumentSource for adapter hosts that never receive storage
 * refs. Missing `kind` (legacy fixture literals) is treated as bytes.
 */
export const documentSourceBytesOnlyService: DocumentSourceService = {
  bytes: (document) =>
    document.kind === 'storage'
      ? Effect.fail(
          makeParserDependencyError({
            service: 'document-source',
            operation: 'bytes',
            code: 'storage_refs_unsupported',
            retryable: false,
          }),
        )
      : Effect.succeed(document.bytes),
};

export type ParserDependencyService =
  | 'http'
  | 'page-source'
  | 'text-layer'
  | 'secrets'
  | 'document-source';

/** Redacted host failure. Bytes, secrets, URLs, and request objects never enter this value. */
export class ParserDependencyError extends Data.TaggedError('ParserDependencyError')<{
  readonly service: ParserDependencyService;
  readonly operation: string;
  readonly code: string;
  readonly retryable: boolean;
}> {}

const parserDependencyErrorCauses = new WeakMap<ParserDependencyError, unknown>();

export function makeParserDependencyError(
  fields: ConstructorParameters<typeof ParserDependencyError>[0],
  cause?: unknown,
): ParserDependencyError {
  const error = new ParserDependencyError(fields);
  if (cause !== undefined) parserDependencyErrorCauses.set(error, cause);
  return error;
}

/** Recover a private host cause at an imperative compatibility boundary. */
export function parserDependencyErrorCause(error: unknown): unknown {
  return error instanceof ParserDependencyError
    ? (parserDependencyErrorCauses.get(error) ?? error)
    : error;
}
